import { createContext, useContext, useEffect, useReducer, type ReactNode } from 'react'
import type { GardenState, GardenAction, Container } from '../types'

const STORAGE_KEY = 'tinyplanter_plan'

const initialState: GardenState = {
  region: null,
  preferredSunlight: null,
  goal: null,
  containers: [],
  savedPlantIds: [],
}

function loadState(): GardenState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return { ...initialState, ...JSON.parse(raw) }
  } catch {}
  return initialState
}

function gardenReducer(state: GardenState, action: GardenAction): GardenState {
  switch (action.type) {
    case 'SET_MVP_CONDITIONS':
      return {
        ...state,
        region: action.region,
        preferredSunlight: action.sunlight,
        goal: action.goal,
      }

    case 'ADD_CONTAINER':
      return { ...state, containers: [...state.containers, action.container] }

    case 'UPDATE_CONTAINER':
      return {
        ...state,
        containers: state.containers.map(c => c.id === action.container.id ? action.container : c),
      }

    case 'REMOVE_CONTAINER':
      return { ...state, containers: state.containers.filter(c => c.id !== action.id) }

    case 'ADD_PLANT_TO_CONTAINER': {
      return {
        ...state,
        containers: state.containers.map(c =>
          c.id === action.containerId && !c.plantIds.includes(action.plantId)
            ? { ...c, plantIds: [...c.plantIds, action.plantId] }
            : c
        ),
      }
    }

    case 'REMOVE_PLANT_FROM_CONTAINER':
      return {
        ...state,
        containers: state.containers.map(c =>
          c.id === action.containerId
            ? { ...c, plantIds: c.plantIds.filter(id => id !== action.plantId) }
            : c
        ),
      }

    case 'TOGGLE_SAVED_PLANT':
      return {
        ...state,
        savedPlantIds: state.savedPlantIds.includes(action.plantId)
          ? state.savedPlantIds.filter(id => id !== action.plantId)
          : [...state.savedPlantIds, action.plantId],
      }

    case 'CLEAR_GARDEN':
      return initialState

    default:
      return state
  }
}

interface GardenContextValue {
  state: GardenState
  dispatch: React.Dispatch<GardenAction>
}

const GardenContext = createContext<GardenContextValue | null>(null)

export function GardenProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(gardenReducer, undefined, loadState)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch {}
  }, [state])

  return <GardenContext.Provider value={{ state, dispatch }}>{children}</GardenContext.Provider>
}

export function useGarden() {
  const ctx = useContext(GardenContext)
  if (!ctx) throw new Error('useGarden must be used inside GardenProvider')
  return ctx
}

export function makeContainerId() {
  return `container-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

export function getContainerPlantCount(container: Container): number {
  return container.plantIds.length
}
